import { useTranslation } from 'react-i18next';
import {
  ActionsColumn as ActionsColumnCommon,
  ActionType,
  IconType,
} from '@otp/ui';
import { Archive, Assign, Clone, Configure, Schedule } from '@otp/icons';
import { SetTemplateDefault } from './set-default';
import { useAuthContext } from 'src/context/auth.context';
import styles from './index.module.css';
import React from 'react';
import { ActionModals, WttpTemplateAction } from './actions-modals';
import { PartialTemplateType } from 'src/components/wttp/templates/types';
import {
  Actions,
  AppArea,
  Subjects,
  WttpTemplateStatus,
} from 'src/graphql/generated/graphql';
import { useCan } from 'src/utils/can';

type ActionsColumProps = {
  wttpTemplate: PartialTemplateType;
  onActionFinished?: (action: WttpTemplateAction) => void;
  isGlobalTemplatesOnly: boolean;
};

export const ActionsColumn = ({
  wttpTemplate,
  onActionFinished,
  isGlobalTemplatesOnly,
}: ActionsColumProps) => {
  const { t } = useTranslation();
  const { isAdmin, isOtpPro, user } = useAuthContext();
  const [showModal, setShowModal] = React.useState<WttpTemplateAction>();
  const { can } = useCan();

  const userOrganizationId = user?.organizationId;
  const isUserOrganizationTemplate =
    userOrganizationId === wttpTemplate?.organizationId;

  const hasEditPermissions = can({
    action: Actions.Write,
    subject: Subjects.Templates,
    appArea: AppArea.Wttp,
  });

  const canSchedule = can({
    action: Actions.Schedule,
    subject: Subjects.Templates,
    appArea: AppArea.Wttp,
  });
  const canEdit =
    (isAdmin && hasEditPermissions) ||
    (hasEditPermissions && isUserOrganizationTemplate);
  const canArchive = canEdit;
  const canClone = hasEditPermissions;
  const canActivate = canEdit && wttpTemplate.isActive;
  const canAssign =
    canEdit &&
    wttpTemplate.isActive &&
    wttpTemplate.status === WttpTemplateStatus.Active;
  const canSetDefault = canAssign;

  const actions = [
    canEdit && {
      id: 'edit',
      label: t('common.edit'),
      icon: <Configure />,
      onClick: () => setShowModal(WttpTemplateAction.Edit),
    },
    canClone && {
      id: 'clone',
      label: t('common.clone'),
      icon: <Clone />,
      onClick: () => setShowModal(WttpTemplateAction.Clone),
    },
    canActivate && {
      id: 'activate',
      label:
        wttpTemplate.status === WttpTemplateStatus.Active
          ? t('common.set-as-draft')
          : t('common.activate'),
      icon: <Archive />,
      onClick: () => {
        const action =
          wttpTemplate.status === WttpTemplateStatus.Active
            ? WttpTemplateAction.Deactivate
            : WttpTemplateAction.Activate;

        if (
          action === WttpTemplateAction.Deactivate &&
          (wttpTemplate.isDefault || !!wttpTemplate.usedByStoreCount)
        ) {
          return setShowModal(WttpTemplateAction.ArchiveAlert);
        }

        setShowModal(action);
      },
    },
    canSchedule && {
      id: 'schedule',
      label: t('common.schedule'),
      icon: <Schedule />,
      onClick: () => setShowModal(WttpTemplateAction.Schedule),
    },
    canAssign && {
      id: 'assign',
      label: t('common.assign'),
      icon: <Assign />,
      onClick: () => setShowModal(WttpTemplateAction.Assign),
    },
    canArchive && {
      id: 'archive',
      label: wttpTemplate.isActive ? t('common.delete') : t('common.restore'),
      icon: <Archive />,
      onClick: () => {
        if (wttpTemplate.isDefault || !!wttpTemplate.usedByStoreCount) {
          return setShowModal(WttpTemplateAction.ArchiveAlert);
        }

        setShowModal(
          wttpTemplate.isActive
            ? WttpTemplateAction.Archive
            : WttpTemplateAction.Unarchive,
        );
      },
    },
  ].filter(Boolean) as ActionType[];

  const icons = [
    canSetDefault && {
      icon: (
        <SetTemplateDefault
          wttpTemplate={wttpTemplate}
          onSubmitted={() => onActionFinished?.(WttpTemplateAction.SetDefault)}
          isGlobalTemplatesOnly={isGlobalTemplatesOnly}
          isUserAdmin={isAdmin}
        />
      ),
      onClick: () => {},
    },
  ].filter(Boolean) as IconType[];

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <ActionsColumnCommon
        actions={actions}
        icons={icons}
        className="justify-end"
        classes={{ menu: styles.actionsColumn }}
      />
      {showModal && (
        <ActionModals
          wttpTemplate={wttpTemplate}
          onClose={() => setShowModal(undefined)}
          action={showModal}
        />
      )}
    </div>
  );
};
